import { useEffect, useState } from "react";
import { API } from "@/lib/api";
import { ImageIcon, Loader2 } from "lucide-react";

export function PlaceholderThumb({ className = "" }) {
  return (
    <div className={`flex items-center justify-center bg-[#e7e2d8] text-[#9a917f] ${className}`}>
      <ImageIcon className="w-8 h-8" />
    </div>
  );
}

/* Loads a protected image and renders it from a blob URL.
   Uses fetch (not axios) so a 404/expired thumbnail resolves cleanly without
   axios touching the XHR's responseText on a blob response.
   Auto-retries on failure (SharePoint may still be processing a fresh upload),
   showing the spinner between attempts and only falling back to the placeholder
   once retries are exhausted. Mounted with key={src} so state resets per source. */
export function AuthImage({ src, alt, className, maxRetries = 5, retryDelay = 3000 }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let active = true;
    let obj = null;
    let timer = null;
    let attempt = 0;
    const token = localStorage.getItem("jackstone_token");
    const load = () => {
      fetch(`${API}${src}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      })
        .then(async (res) => {
          if (!res.ok) throw new Error(`thumbnail ${res.status}`);
          const blob = await res.blob();
          if (!active) return;
          obj = URL.createObjectURL(blob);
          setUrl(obj);
        })
        .catch(() => {
          if (!active) return;
          if (attempt < maxRetries) {
            attempt += 1;
            timer = setTimeout(load, retryDelay);
          } else {
            setErr(true);
          }
        });
    };
    load();
    return () => {
      active = false;
      if (timer) clearTimeout(timer);
      if (obj) URL.revokeObjectURL(obj);
    };
  }, [src, maxRetries, retryDelay]);

  if (err) return <PlaceholderThumb className={className} />;
  if (!url)
    return (
      <div className={`flex items-center justify-center bg-[#f0ece3] ${className}`}>
        <Loader2 className="w-6 h-6 animate-spin text-[#9a917f]" />
      </div>
    );
  return <img src={url} alt={alt} className={className} />;
}
